/** Scoped image transport: authenticated JSON control requests; image bytes only to the presigned R2 URL.
 * The direct PUT carries exactly server-required headers and browser-owned Content-Length, never app credentials.
 */
import {
  SHOPIFY_MEDIA_UPLOAD_GRANT_OPERATION,
  SHOPIFY_MEDIA_UPLOAD_FINALIZE_OPERATION,
  type ShopifyOperationDefinition,
} from '@testfeed/shopify-contracts'
import { shopifyApiFetch, type ShopifyAppBridge } from './shopifyApiFetch'
import { throwMerchantFacingApiError } from './shopifyApiError'

async function controlRequest<Input, Output>(
  shopify: ShopifyAppBridge,
  operation: ShopifyOperationDefinition<Input, Output>,
  input: Input,
  signal: AbortSignal,
): Promise<Output> {
  const response = await shopifyApiFetch(shopify, operation.path, {
    init: {
      method: operation.method,
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
      signal,
    },
  })
  if (!response.ok) return throwMerchantFacingApiError(response, operation.id)
  const result = operation.responseSchema.safeParse(await response.json())
  if (!result.success) throw new Error('STUDY_BUILDER_UPLOAD_INVALID_RESPONSE')
  signal.throwIfAborted()
  return result.data
}

export async function uploadStudyBuilderImage(
  shopify: ShopifyAppBridge,
  destination: { draftId: string; studyId?: string; key: string },
  file: File,
  signal: AbortSignal,
) {
  const grant = await controlRequest(
    shopify,
    SHOPIFY_MEDIA_UPLOAD_GRANT_OPERATION,
    {
      ...destination,
      fileName: file.name,
      contentType: file.type,
      size: file.size,
    },
    signal,
  )
  signal.throwIfAborted()
  const uploaded = await window.fetch(grant.uploadUrl, {
    method: 'PUT',
    headers: grant.requiredHeaders,
    body: file,
    signal,
    credentials: 'omit',
  })
  signal.throwIfAborted()
  // A repeated create-only PUT may report an existing object. Finalization verifies that exact signed destination.
  if (!uploaded.ok && uploaded.status !== 412) throw new Error(`STUDY_BUILDER_UPLOAD_PUT_FAILED: ${uploaded.status}`)
  return controlRequest(shopify, SHOPIFY_MEDIA_UPLOAD_FINALIZE_OPERATION, { receipt: grant.receipt }, signal)
}
